import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { meetingTypes } from "../../config/meetingOptions";
import "./ConversationPrompt.css";
import { createMeeting, generateMeetingFromPrompt } from "../../api/meetingApi";
import briefcaseIcon from "../../assets/icons/conversation-starter-icons/icons8-briefcase.svg";
import languageIcon from "../../assets/icons/conversation-starter-icons/icons8-language.svg";
import earthIcon from "../../assets/icons/conversation-starter-icons/icons8-earth.svg";
import coffeeIcon from "../../assets/icons/conversation-starter-icons/icons8-coffee.svg";
import salesIcon from "../../assets/icons/conversation-starter-icons/icons8-sales.svg";
import magicIcon from "../../assets/icons/conversation-starter-icons/icons8-magic.svg";
import podiumIcon from "../../assets/icons/conversation-starter-icons/icons8-podium.svg";
import brainIcon from "../../assets/icons/conversation-starter-icons/icons8-brain.svg";
import puzzleIcon from "../../assets/icons/conversation-starter-icons/icons8-puzzle.svg";
import supportIcon from "../../assets/icons/conversation-starter-icons/icons8-support.svg";
import conferenceIcon from "../../assets/icons/conversation-starter-icons/icons8-conference-foreground-selected.svg";
import templateIcon from "../../assets/icons/conversation-starter-icons/icons8-template.svg";
import { formatNice } from "../../utils/formatNice";
import {
  ArrowRight,
  ArrowUp,
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  Globe2,
  Paperclip,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useToast } from "../../components/Toaster/use-toast";
import useDocumentUploader from "../../hooks/useDocumentUploader";
import AttachmentBadges from "../../components/Attachments/AttachmentBadges";
import { WEB_ACCEPT_ATTR } from "../../utils/uploadLimits";
import {
  isRealtimeUpgradeRequired,
  realtimeUpgradeMessage,
} from "../../utils/realtimeUpgradeState";


const ConversationPrompt = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState(null); // holds AI response
  const [showBadges, setShowBadges] = useState(true);
  const [payload, setPayload] = useState(null);
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

 const { toast} = useToast()

 const {
  documents,     // UI metadata chips
  docFiles,      // File[]
  uploadErrors,
  isParsingDocs,
  handleFiles,
  removeDocument,
  buildFormData,
  clearAll
} = useDocumentUploader({ toast });


  const handleSelect = (type) => {
    navigate("/practice-setup", { state: { meetingType: type } });
  };

  const runGeneration = async (inputPrompt) => {
    const nextPrompt = String(inputPrompt || "").trim();
    if (!nextPrompt || isLoading || isParsingDocs) return;

    setIsLoading(true);
    setShowBadges(false);

    try {
      const previousPayload = payload || null;
      const fd = buildFormData({
        prompt: nextPrompt,
        previousPayload,
      });

      const result = await generateMeetingFromPrompt(fd);

      setPayload(result.meetingPayload);
      setResponse(result);
      setPrompt("");
      clearAll();
    } catch (err) {
      console.error("❌ Error generating meeting:", err);
      const backendError =
        err.response?.data?.error ||
        err.error ||
        err.message ||
        "Something went wrong. Try again.";
      if (isRealtimeUpgradeRequired(err) || isRealtimeUpgradeRequired(backendError)) {
        setResponse({
          upgradeRequired: true,
          message: realtimeUpgradeMessage(backendError),
        });
        return;
      }
      setResponse({ error: backendError });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    void runGeneration(prompt);
  };
  
  const getBadgeIcon = (value) => {
    const svgIconByType = {
      custom: magicIcon,
      "job-interview": briefcaseIcon,
      "ielts-speaking-interview": languageIcon,
      networking: earthIcon,
      "small-talk": coffeeIcon,
      "sales-call": salesIcon,
      "performance-review": brainIcon,
      debate: podiumIcon,
      recruiting: puzzleIcon,
      "customer-support": supportIcon,
      "team-meeting": conferenceIcon,
      "client-presentation": templateIcon,
    };

    const iconSrc = svgIconByType[value];
    if (iconSrc) {
      return (
        <img
          src={iconSrc}
          alt=""
          aria-hidden="true"
          className="conversation-type-icon conversation-type-icon--svg"
          loading="lazy"
        />
      );
    }

    return (
      <span className="conversation-type-icon" aria-hidden="true">
        •
      </span>
    );
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isLoading || isParsingDocs) return;
    await handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (isLoading || isParsingDocs) return;
    setIsDragOver(true);
  };

  const onDragLeave = () => setIsDragOver(false);

  const renderSummary = (payload) => {
    if (!payload) return null;

    const {
      meetingType,
      industry,
      userRole,
      speakingWith,
      duration,
      aiBehavior,
      languageLabel,
      goals,
      customGoals,
      additionalInfo,
    } = payload;

    const focusAreas = [...(Array.isArray(goals) ? goals : []), ...(Array.isArray(customGoals) ? customGoals : [])]
      .map((goal) => String(goal || "").trim())
      .filter((goal, index, items) =>
        goal && items.findIndex((item) => item.toLowerCase() === goal.toLowerCase()) === index
      );
    const hasGoals = focusAreas.length > 0;
    const hasDescription =
      typeof additionalInfo === "string" && additionalInfo.trim() !== "";
    const chips = [
      { Icon: Clock3, value: `${duration || 10} min` },
      { Icon: Globe2, value: languageLabel || "English" },
      { Icon: BriefcaseBusiness, value: formatNice(industry) },
      { Icon: UserRound, value: formatNice(aiBehavior) },
    ].filter((chip) => chip.value);

    return (
      <div className="cp-summary-card">
        <div className="cp-summary-header">
          <h3 className="cp-summary-title">{formatNice(meetingType)}</h3>
          <span className="cp-ready-badge">
            Ready
            <CheckCircle2 size={19} aria-hidden="true" />
          </span>
        </div>

        {hasDescription && (
          <p className="cp-summary-description">{additionalInfo.trim()}</p>
        )}

        <div className="cp-summary-chips" aria-label="Conversation details">
          {chips.map(({ Icon, value }, index) => (
            <span className="cp-summary-chip" key={`${value}-${index}`}>
              <Icon size={15} aria-hidden="true" />
              {value}
            </span>
          ))}
        </div>

        <div className="cp-summary-divider" />

        <div className="cp-role-flow">
          <div className="cp-role-item">
            <UserRound className="cp-role-icon" size={23} aria-hidden="true" />
            <div className="cp-role-copy">
              <span className="cp-role-label">Your Role</span>
              <strong className="cp-role-value">{formatNice(userRole)}</strong>
            </div>
          </div>

          <ArrowRight className="cp-role-arrow" size={23} aria-hidden="true" />

          <div className="cp-role-item">
            <UsersRound className="cp-role-icon" size={23} aria-hidden="true" />
            <div className="cp-role-copy">
              <span className="cp-role-label">Speaking With</span>
              <strong className="cp-role-value">{formatNice(speakingWith)}</strong>
            </div>
          </div>
        </div>

        {hasGoals && (
          <section className="cp-focus-section">
            <h4>Focus Areas</h4>
            <ul className="cp-focus-list">
              {focusAreas.map((goal, index) => (
                <li key={`${goal}-${index}`}>
                  <CheckCircle2 size={20} aria-hidden="true" />
                  <span>{goal}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <button
          type="button"
          className="cp-summary-primary"
          onClick={async () => {
            try {
              if (!payload) return;
              const fd = buildFormData(
                {
                  ...payload,
                  createdWithAi: true,
                },
                { includeFiles: false } //  prevents re-upload + re-digest
              );
              const meeting = await createMeeting(fd);
              navigate(`/meeting/${meeting.id}`);
            } catch (err) {
              console.error("❌ Failed to start meeting:", err);
              if (isRealtimeUpgradeRequired(err)) {
                navigate("/pricing");
                return;
              }
              alert("Failed to start the practice meeting. Please try again.");
            }
          }}
        >
          Start Practice
        </button>

        <button
          type="button"
          className="cp-summary-secondary"
          onClick={() => navigate("/practice-setup")}
        >
          Create Manually Instead
        </button>
      </div>
    );
  };
  const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
  const showSuggestions = !isLoading && response?.status === "ready" && suggestions.length > 0;
  const responseNeedsUpgrade =
    Boolean(response?.upgradeRequired) || isRealtimeUpgradeRequired(response?.error);
  const isMobile = () =>
  window.matchMedia("(pointer: coarse)").matches; 


  const applySuggestion = (suggestion) => {
    setPrompt("");
    void runGeneration(suggestion);
  };
  return (
    <div className="conversation-type-container">
      <div className="conversation-type-content-pre">
        {/* 🔑 Hide these while waiting for AI */}
        {!response && !isLoading && (
          <>
            <h1 className="conversation-type-header">
              Choose your conversation type
            </h1>
            <p className="conversation-type-subheader">
              What do you want to get better at?
            </p>
          </>
        )}

        {showBadges ? (
          <div className="conversation-grid-wrapper">
            <div className="conversation-type-grid">
              {meetingTypes.map((type) => (
                <button
                  key={type.value}
                  className="conversation-type-badge"
                  onClick={() => handleSelect(type.value)}
                >
                  {getBadgeIcon(type.value)}
                  <span>{type.label}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="conversation-response">
            {isLoading && (
              <div className="cp-thinking-state" role="status" aria-live="polite">
                <span>
                  {payload ? "Updating your conversation" : "Generating your conversation"}
                </span>
                <span className="cp-thinking-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            )}
            {!isLoading && response && (
              <div className="ai-response-box">
                {responseNeedsUpgrade && (
                  <div className="cp-upgrade-state" role="status">
                    <p className="cp-upgrade-message">
                      {realtimeUpgradeMessage(response.error || response.message)}
                    </p>
                    <button
                      type="button"
                      className="cp-upgrade-button"
                      onClick={() => navigate("/pricing")}
                    >
                      Upgrade
                    </button>
                  </div>
                )}

                {response.error && !responseNeedsUpgrade && <p className="error-text">{response.error}</p>}

                {!response.status && !response.error && !responseNeedsUpgrade && (
                  <p>No structured response received from AI.</p>
                )}

                {response.message && !responseNeedsUpgrade && (
                  <div className="ai-response-header">
                    <p className="ai-response">{response.message}</p>
                  </div>
                )}

                {response.status === "ready" &&
                  renderSummary(response.meetingPayload)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom input bar */}
      <form className="conversation-input-bar" onSubmit={handleSubmit}>
  <div
    className={`cp-input-shell ${isDragOver ? "is-dragover" : ""}`}
    onDrop={onDrop}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
  >
    {showSuggestions && (
      <div className="cp-suggestions" aria-label="Prompt suggestions">
        <div className="cp-suggestions-track">
          {suggestions.map((s, i) => (
            <button
              key={`${i}-${s}`}
              type="button"
              className="cp-suggestion-pill"
              onClick={() => applySuggestion(s)}
              title="Apply this update"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    )}

{(documents.length > 0 || uploadErrors.length > 0) && (
  <div className="cp-attachments">
    <AttachmentBadges
      documents={documents}
      docFiles={docFiles}
      onRemove={removeDocument}
      showThumbs
    />
    {uploadErrors.length > 0 && (
      <div className="cp-upload-errors">
        {uploadErrors.map((err, i) => (
          <div key={i} className="cp-upload-error">{err}</div>
        ))}
      </div>
    )}
  </div>
)}


    {/* ✅ unified row */}
    <div className="cp-input-row">
      <button
        type="button"
        className="cp-icon-btn"
        onClick={() => fileInputRef.current?.click()}
        disabled={isLoading || isParsingDocs}
        title="Attach documents"
        aria-label="Attach documents"
      >
        <Paperclip size={22} color={"var(--foreground)"} />
      </button>

      <textarea
        value={prompt}
        placeholder="e.g., Create me a mock job interview"
        onChange={(e) => {
          setPrompt(e.target.value);
          e.target.style.height = "auto";
          e.target.style.height = `${e.target.scrollHeight}px`;
        }}
    onKeyDown={(e) => {
  const mobile = isMobile();

  if (e.key === "Enter") {
    // ---- MOBILE BEHAVIOR ----
    if (mobile && !e.shiftKey) {
      // allow normal new line
      return;
    }

    // ---- DESKTOP BEHAVIOR ----
    if (!mobile && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  }
}}

        disabled={isLoading}
        className="cp-textarea"
      />

      <button
        type="submit"
        className="cp-send"
        disabled={isLoading || isParsingDocs || !prompt.trim()}
        aria-label="Send"
        title="Send"
      >
        {isLoading ? "..." : <ArrowUp />}
      </button>
    </div>

    {/* hidden input */}
    <input
      ref={fileInputRef}
      type="file"
      multiple
      accept={WEB_ACCEPT_ATTR}
      style={{ display: "none" }}
      onChange={async (e) => {
        if (e.target.files?.length) await handleFiles(e.target.files);
        e.target.value = "";
      }}
    />

    {isDragOver && <div className="cp-drop-overlay">Drop files to attach</div>}
  </div>
</form>

    </div>
  );
};

export default ConversationPrompt;
